import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuthContext } from 'context/AuthContext';
import Header from 'components/Header';
import Tabs from 'components/Tabs';
import TextInput from 'components/TextInput';
import Button from 'components/Button';
import Dropdown from 'components/Dropdown';
import Popup from 'components/Popup';
import Table from 'components/Table';
import axios, { resumeOwnSession } from 'config/axios';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';

import Students from './Students';
import './index.css';

const TABS = ['Profile', 'Students'];
const ROLES = [
    'Admin',
    'Tutor'
];

function Instructor() {
    const [tutorData, setTutorData] = useState(null);
    const [originalData, setOriginalData] = useState(null);
    const [students, setStudents] = useState([]);
    const [updateStudents, setUpdateStudents] = useState(false);
    const [searchStudent, setSearchStudent] = useState(null);
    const [tabIndex, setTabIndex] = useState(0);
    const [update, setUpdate] = useState(false);
    const [deleteAccount, setDeleteAccount] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();
    const { instructorId } = useParams();

    const { user } = useAuthContext();
    const deleteAccountRef = useOutsideClickHandler(() => setDeleteAccount(false));
    const errorRef = useOutsideClickHandler(() => setError(''));

    const updateTutorInfo = () => {
        axios.post(`tutors/tutors/${instructorId}/update/`, {
                ...tutorData
            })
            .then(async res => {
                // Only sent when an admin changed their own email here (see Profile).
                const { customToken, ...tutor } = res.data;
                if (customToken) await resumeOwnSession(customToken).catch(() =>
                    setError('Your email was updated. Please sign in again with your new email.'));
                setOriginalData(tutor);
                setTutorData(tutor);
            })
            .catch(error => {
                setTutorData(originalData);
                setError('Failed to update instructor.');
                console.log(error);
            });
    }

    const deleteTutor = () => {
        axios.post(`tutors/tutors/${instructorId}/delete/`)
            .then(res => {
                setDeleteAccount(false);
                navigate('/instructors', { replace: true });
            })
            .catch(error => {
                setDeleteAccount(false);
                setError('Failed to delete instructor.');
                console.log(error);
            });
    }

    useEffect(() => {
        axios.get(`tutors/tutors/${instructorId}/`)
            .then(res => {
                setOriginalData(res.data.tutorInfo);
                setTutorData(res.data.tutorInfo);
                setStudents(res.data.students);
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    const deleteAccountPopup = (
        <Popup
            title='Delete instructor'
            primaryButtonText='Delete'
            primaryButtonOnClick={deleteTutor}
            secondaryButtonText='Cancel'
            secondaryButtonOnClick={() => setDeleteAccount(false)}
            innerRef={deleteAccountRef}
        >
            <p>
                Are you sure you want to delete this instructor?
                <br/><br/>
            </p>
        </Popup>
    );

    const failedPopup = (
        <Popup
            title='Failed'
            primaryButtonText='Close'
            primaryButtonOnClick={() => setError('')}
            innerRef={errorRef}
        >
            {error}
        </Popup>
    );

    useEffect(() => {
        setUpdateStudents(false);
    }, [tabIndex]);

    return (
        <div className='instructor'>
            <Helmet>
                <title>
                    Instructor {tutorData ? `${tutorData.firstName} ${tutorData.lastName}` : ''} - Test Center Admin
                </title>
            </Helmet>
            <Header
                title={tutorData ? `${tutorData.firstName} ${tutorData.lastName}` : null}
                prevRoutes={[{ name: 'All Instructors', path: '/instructors' }]}
            />
            <div className='instructor__tabs'>
                <Tabs
                    tabs={TABS}
                    tabIndex={tabIndex}
                    onSelect={TABS.map((_tab, t) => (
                        () => {
                            setTabIndex(t);
                        }
                    ))}
                />
                {user.role === 'Admin' && TABS[tabIndex] === 'Students' && (updateStudents
                    ?   <div className='instructor__tab'>
                            {searchStudent !== null
                            ?   <TextInput
                                    placeholder='search student'
                                    value={searchStudent}
                                    onChange={event => setSearchStudent(event.target.value)}
                                />
                            :   <div></div>
                            }
                            <Button secondary onClick={() => {
                                setUpdateStudents(false);
                                setSearchStudent(null);
                            }}>
                                Save
                            </Button>
                        </div>
                    :   <Button onClick={() => {
                            setUpdateStudents(true);
                            setSearchStudent('');
                        }}>
                            Assign Students
                        </Button>
                )}
            </div>
            {(tutorData && TABS[tabIndex] === 'Profile')
            ?   <div className='instructor'>
                    {error && failedPopup}
                    {deleteAccount && deleteAccountPopup}
                    <div className='instructor__profile'>
                        <Dropdown
                            label='Role'
                            data={ROLES}
                            value={tutorData.role}
                            disabled={!update}
                            onSelect={value => setTutorData({
                                ...tutorData,
                                role: value
                            })}
                        />
                        <div className='instructor__grid'>
                            <TextInput
                                label='First name'
                                value={tutorData.firstName}
                                disabled={!update}
                                onChange={event => setTutorData({
                                    ...tutorData,
                                    firstName: event.target.value
                                })}
                            />
                            <TextInput
                                label='Last name'
                                value={tutorData.lastName}
                                disabled={!update}
                                onChange={event => setTutorData({
                                    ...tutorData,
                                    lastName: event.target.value
                                })}
                            />
                            <TextInput
                                label='Email'
                                value={tutorData.email}
                                disabled={!update}
                                onChange={event => setTutorData({
                                    ...tutorData,
                                    email: event.target.value
                                })}
                            />
                            <TextInput
                                label='Phone number'
                                value={tutorData.phoneNumber}
                                disabled={!update}
                                onChange={event => {
                                    const value = event.target.value.replace(/[^\d]/g, '');
                                    if (value.length <= 15) {
                                        setTutorData({
                                            ...tutorData,
                                            phoneNumber: value
                                        });
                                    }
                                }}
                            />
                            {/* <TextInput
                                label='Password'
                                value={tutorData.password}
                                disabled={!update}
                                onChange={event => setTutorData({
                                    ...tutorData,
                                    password: event.target.value
                                })}
                            /> */}
                        </div>
                    </div>
                    <Button
                        className='instructor__update'
                        secondary={update}
                        onClick={() => {
                            if (update) updateTutorInfo();
                            setUpdate(!update);
                        }}
                    >
                        {update ? 'Save' : 'Edit'}
                    </Button>
                    <Button
                        className='instructor__delete'
                        onClick={() => setDeleteAccount(true)}
                    >
                        Delete Account
                    </Button>
                </div>
            :   TABS[tabIndex] === 'Students'
            ?   <Students
                    students={searchStudent ? students.filter(student => (
                        student.inClass ||
                        student.email?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.studentId?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.firstName?.toLowerCase().includes(searchStudent.toLowerCase()) || 
                        student.lastName?.toLowerCase().includes(searchStudent.toLowerCase())
                    )) : students}
                    setStudents={setStudents}
                    updateStudents={updateStudents}
                />
            :   <></>
            }
        </div>
    );
}

export default Instructor;